import moment from 'moment';
import React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import classNames from 'classnames';
import { UiDownloadAlt } from '@vyaguta/vyaguta-icons/ui';

import Loading from 'components/common/loading/Loading';
import FilterPills from 'components/common/filterPills';
import SmallDatePicker from 'components/common/datepicker/SmallDatePicker';

import { statusLabels, requestPillStatus, requestPillStatusColor } from 'constants/changeLeaveIssuer';
import { SMALL_DATEPICKER_DATE_FORMAT } from 'constants/appConstants';

import { getFormattedDate } from 'utils/date';

import type { ChangeLeaveIssuerFilters, LeaveIssuerRequestSummary } from '../interfaces';

interface Props {
  isLoading: boolean;
  requestSummary: LeaveIssuerRequestSummary;
  filters: ChangeLeaveIssuerFilters;
  setFilters: Dispatch<SetStateAction<ChangeLeaveIssuerFilters>>;
  isFilterChanged: boolean;
  isFilterApplied: boolean;
  setIsFilterChanged: Dispatch<SetStateAction<boolean>>;
  isStartDateChanged: boolean;
  isEndDateChanged: boolean;
  handleApplyFilter: () => void;
  handleReset: () => void;
  handleExportClick: () => void | Promise<void>;
  isExporting: boolean;
  hasData: boolean;
  onBulkApprove: () => void | Promise<void>;
  isBulkApproving: boolean;
  hasSelectedRequests: boolean;
}

export default function ChangeLeaveIssuerFilters({
  isLoading,
  requestSummary,
  filters,
  setFilters,
  isFilterChanged,
  isFilterApplied,
  setIsFilterChanged,
  isStartDateChanged,
  isEndDateChanged,
  handleApplyFilter,
  handleReset,
  handleExportClick,
  isExporting,
  hasData,
  onBulkApprove,
  isBulkApproving,
  hasSelectedRequests,
}: Props) {
  return (
    <>
      <div className="full-scope-card__header d-flex justify-content-between name-wrap">
        <div className="d-flex align-items-center">
          <h3 className="table-title mrpx-40">Leave Issuer Requests</h3>
          {!isLoading && (
            <FilterPills
              statusLabels={statusLabels}
              statuses={requestPillStatus}
              colorMap={requestPillStatusColor}
              summary={requestSummary}
              onChange={(selectedFilter) => {
                setFilters({ ...filters, status: selectedFilter });
              }}
            />
          )}
        </div>

        <button
          type="submit"
          className={classNames('btn btn--secondary plpx-12 prpx-12', {
            invisible: !hasSelectedRequests,
          })}
          onClick={() => void onBulkApprove()}
          disabled={isBulkApproving}
        >
          {isBulkApproving && <Loading className="p-absolute black" />}
          <span className={classNames({ invisible: isBulkApproving })}>Approve selected</span>
        </button>
      </div>

      <div className="d-flex align-items-center border-bottom-gray ptpx-4 pbpx-4 prpx-4 plpx-4">
        <div className="hpx-45 mlpx-5 d-flex gap-px-8">
          <SmallDatePicker
            date={moment(filters.startDate)}
            displayFormat={SMALL_DATEPICKER_DATE_FORMAT}
            label={'Start Date'}
            onDateChange={(date) => {
              if (!date) {
                return;
              }

              setFilters({ ...filters, startDate: getFormattedDate(date) });
              setIsFilterChanged(true);
            }}
            isOutsideRange={(date) => date.isAfter(filters.endDate)}
            isDark={isStartDateChanged}
            fromEnhancedFilter
            noBorder
          />

          <SmallDatePicker
            date={moment(filters.endDate)}
            displayFormat={SMALL_DATEPICKER_DATE_FORMAT}
            label={'End Date'}
            onDateChange={(date) => {
              if (!date) {
                return;
              }

              setFilters({ ...filters, endDate: getFormattedDate(date) });
              setIsFilterChanged(true);
            }}
            isOutsideRange={(date) => date.isBefore(filters.startDate)}
            isDark={isEndDateChanged}
            fromEnhancedFilter
            noBorder
          />
        </div>
        <div className="d-flex align-items-center margin-left-auto mrpx-16">
          <button
            className={classNames('button m-10 ml-24 wpx-96', {
              'button--primary': !isFilterChanged,
              'button--secondary': isFilterChanged,
            })}
            onClick={handleApplyFilter}
          >
            Apply Filter
          </button>

          <button
            className={classNames('button__reset', {
              'button__reset--active': isFilterApplied,
            })}
            onClick={handleReset}
          >
            Reset
          </button>

          <button
            className="btn btn--outlined-grey export-button prpx-8 plpx-8"
            onClick={() => void handleExportClick()}
            disabled={isExporting || isLoading || !hasData}
          >
            {isExporting ? (
              <div className="export-button loading">
                <Loading />
              </div>
            ) : (
              <>
                <UiDownloadAlt className="download-icon" />
                {'.CSV'}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
